import React from 'react';
import { AlertTriangle, Timer } from 'lucide-react';
import type {
  ProcessingIntervalFigures,
  ProcessingTimesData,
} from '../../../hooks/pipelineTracker/usePipelineReport';
import { num } from './pipelineParts';
import { CHART_PALETTE, HousePanel, HouseStatCard, HouseStatGrid } from './trackerKit';

/**
 * How long a deal takes between milestones.
 *
 * WHY THESE THREE. They are the three Evoke publishes by hand every month, and they are the only
 * timings the export can support: each runs between two milestones that carry their own date
 * column, so they need one snapshot and nothing has to accrue. Every other timing in the February
 * brief measures between stages that have no date, and those need stage history the export does
 * not hold.
 *
 * THE DENOMINATOR IS PART OF THE FIGURE, NOT A FOOTNOTE. These date columns are written through
 * ApplyOnline, which is residential only, so they are absent on most commercial deals. Measured at
 * Evoke, the three intervals cover 251, 187 and 174 deals out of 2,149. A median printed alone
 * would read as the practice's typical deal; printed with "from 251 deals" it reads as what it is.
 * This is the same failure the flow band had, and it is not repeated here.
 *
 * MEDIAN IS THE HEADLINE, MEAN IS SHOWN ANYWAY. A 4-day median against a 159-day maximum means the
 * mean is describing a handful of stalled files rather than the usual case. The mean is still
 * rendered, because their own spreadsheet says "Average" and a figure they cannot reconcile is a
 * figure they will not trust.
 *
 * A CONTRADICTION IS A FINDING. Where the later milestone is dated before the earlier one, the
 * pair is excluded from the statistic and reported on its own. At Evoke that is 40 of 251 pairs,
 * which is worth knowing about the data rather than quietly averaging away.
 */

const LABELS: Record<ProcessingIntervalFigures['key'], string> = {
  submitted_to_conditional: 'Submitted to conditional',
  conditional_to_unconditional: 'Conditional to unconditional',
  unconditional_to_settled: 'Unconditional to settled',
};

const days = (n: number | null): string => (n === null ? '--' : `${num(n)}`);

/** One colour per step, from the house palette, in milestone order. */
const STEP_COLOUR: Record<ProcessingIntervalFigures['key'], string> = {
  submitted_to_conditional: CHART_PALETTE[1],
  conditional_to_unconditional: CHART_PALETTE[3],
  unconditional_to_settled: CHART_PALETTE[2],
};

const Interval: React.FC<{ figures: ProcessingIntervalFigures; totalDeals: number }> = ({
  figures,
  totalDeals,
}) => {
  const measured = figures.measuredDeals;
  const colour = STEP_COLOUR[figures.key];

  if (measured === 0) {
    return (
      <HouseStatCard
        label={LABELS[figures.key]}
        color={colour}
        value={<span className="text-lg text-gray-500">Not measurable</span>}
        footer="No deal in scope carries both dates."
        testId={`interval-${figures.key}`}
      />
    );
  }

  return (
    <HouseStatCard
      label={LABELS[figures.key]}
      color={colour}
      testId={`interval-${figures.key}`}
      value={
        <span className="flex items-baseline gap-1.5">
          <span className="tabular-nums" style={{ color: colour }}>{days(figures.medianDays)}</span>
          <span className="text-xs font-normal tracking-normal text-gray-400">days, median</span>
        </span>
      }
      footer={
        <>
          {/* The denominator sits directly under the number it belongs to. */}
          <span className="block text-[0.6875rem] leading-tight text-gray-500">
            from {num(measured)} of {num(totalDeals)} deals
          </span>
          <span className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-[0.6875rem] text-gray-400">
            <span>mean {days(figures.meanDays)}</span>
            <span>75th {days(figures.p75Days)}</span>
            <span>longest {days(figures.maxDays)}</span>
          </span>
          {figures.disorderedDeals > 0 && (
            <span className="mt-2 flex items-start gap-1.5 text-[0.6875rem] leading-tight text-amber-400">
              <AlertTriangle className="mt-0.5 h-3 w-3 flex-none" />
              <span>
                {num(figures.disorderedDeals)} more dated out of order and are excluded
              </span>
            </span>
          )}
        </>
      }
    />
  );
};

export interface ProcessingBandProps {
  processing: ProcessingTimesData;
  /** The panel's accent: its group's colour on the jump bar. Brand blue by default. */
  accent?: string;
  /** The scope chip, e.g. "As at 12 Sept 2026": the period does not change these timings. */
  chip?: React.ReactNode;
}

const ProcessingBand: React.FC<ProcessingBandProps> = ({ processing, accent, chip }) => {
  const anyMeasured = processing.intervals.some((i) => i.measuredDeals > 0);

  return (
    <HousePanel
      title="How long each step takes"
      icon={Timer}
      color={accent}
      badge={chip}
      blurb="Measured only where both milestone dates exist, so commercial deals are mostly absent."
    >

      <HouseStatGrid count={3}>
        {processing.intervals.map((figures) => (
          <Interval key={figures.key} figures={figures} totalDeals={processing.totalDeals} />
        ))}
      </HouseStatGrid>

      {!anyMeasured && (
        <p className="mt-3 text-[0.8125rem] text-gray-400">
          Nothing in scope carries the milestone dates these timings are measured from. That is the
          expected reading for a commercial-only book, or for a snapshot pulled by stage rather than
          by milestone.
        </p>
      )}
    </HousePanel>
  );
};

export default ProcessingBand;
